import { buildDensityConfig, deriveFromSeed, hotSet, siteSet, sourcesField } from './build-config';
import { clamp, lineDist, mix, mulberry32, ptInTri, smoothstep } from './math';
import type { DensityFieldConfig } from './types';

export { buildDensityConfig, deriveFromSeed };

export function densityField(x: number, y: number, o: DensityFieldConfig): number {
  const { mode, width, height, seed } = o;
  const cx = width * 0.5;
  const cy = height * 0.52;

  if (o.kaleN && o.kaleN > 1) {
    const sec = (2 * Math.PI) / o.kaleN;
    const ddx = x - cx;
    const ddy = y - cy;
    const rr = Math.hypot(ddx, ddy);
    let a = Math.atan2(ddy, ddx);
    a = ((a % sec) + sec) % sec;
    if (a > sec / 2) a = sec - a;
    a += ((o.kaleRot || 0) * Math.PI) / 180;
    x = cx + Math.cos(a) * rr;
    y = cy + Math.sin(a) * rr;
  }

  const dx = x - cx;
  const dy = y - cy;
  const d = Math.hypot(dx, dy);
  const angle = Math.atan2(dy, dx);
  const maxD = Math.hypot(cx, cy);
  const minSide = Math.min(width, height);
  const wave = 0.5 + 0.5 * Math.sin(x * 0.009 + y * 0.006 + seed * 0.001);
  const ring = Math.abs(Math.sin(d * 0.045));
  const ringBand = 1 - smoothstep(0.02, 0.22, Math.abs(ring - 0.04));
  const vignette = 1 - smoothstep(maxD * 0.55, maxD * 1.05, d);
  const D = o.derived ?? { outcomes: 2, skew: 0.5, phase: 0, turbulence: 0.5 };

  switch (mode) {
    case 'polarization':
      return sourcesField(x, y, o);
    case 'drift': {
      const t = x / width;
      const uniform = 0.28 + wave * 0.18;
      const tg = { x: width * 0.78, y: cy };
      const cluster = 1 - smoothstep(0, maxD * 0.42, Math.hypot(x - tg.x, y - tg.y));
      return clamp(mix(uniform, cluster * 0.95, t * t * (3 - 2 * t)), 0, 1);
    }
    case 'defection':
      return sourcesField(x, y, o);
    case 'wave': {
      const phase = x / width - 0.5 + Math.sin(y * 0.008 + D.phase) * 0.04;
      const wf = Math.exp(-((phase * 7) ** 2)) * 1.6;
      const behind = smoothstep(-0.12, 0.06, phase);
      return clamp(0.08 + behind * 0.55 + wf * 0.45 + wave * 0.05, 0, 1);
    }
    case 'round2': {
      const topY = height * 0.27;
      const botY = height * 0.73;
      const tc = width * (0.5 + (D.skew - 0.5) * 0.2);
      const bc = width * (0.5 - (D.skew - 0.5) * 0.6);
      const top = 1 - smoothstep(0, maxD * 0.32, Math.hypot(x - tc, y - topY));
      const bot = 1 - smoothstep(0, maxD * 0.26, Math.hypot(x - bc, y - botY));
      const div = 1 - smoothstep(0, height * 0.03, Math.abs(y - height * 0.5));
      return clamp(0.05 + top * 0.55 + bot * 0.7 - div * 0.3 + wave * 0.05, 0, 1);
    }
    case 'fluting': {
      const fl = D.outcomes * 4 + 8;
      const flute = 0.5 + 0.5 * Math.cos((x / width) * Math.PI * fl);
      const inset = 1 - smoothstep(0, height * 0.04, Math.min(y, height - y));
      const hf = 1 - smoothstep(height * 0.4, height * 0.5, Math.abs(y - cy)) * 0.2;
      return clamp(0.18 + flute * 0.6 * hf - inset * 0.3 + wave * 0.05, 0, 1);
    }
    case 'tholos': {
      const phi = 1.618033988749;
      const half = minSide * 0.5;
      let total = 0;
      let r = half * 0.05;
      for (let i = 0; i < 7 && r < half * 0.95; i++) {
        total += (1 - smoothstep(0, 10, Math.abs(d - r))) * (0.85 - i * 0.05);
        r *= phi * 0.62 + 0.02 * i;
      }
      const spokes = 0.5 + 0.5 * Math.cos(angle * 12);
      const sf = 1 - smoothstep(half * 0.2, half * 0.5, d);
      return clamp(0.06 + total * 0.35 + spokes * 0.15 * sf + wave * 0.04, 0, 1);
    }
    case 'pediment': {
      const apex = [width * 0.5, height * 0.18] as const;
      const left = [width * 0.12, height * 0.82] as const;
      const right = [width * 0.88, height * 0.82] as const;
      const ed = Math.min(
        lineDist(x, y, apex[0], apex[1], left[0], left[1]),
        lineDist(x, y, apex[0], apex[1], right[0], right[1]),
        lineDist(x, y, left[0], left[1], right[0], right[1]),
      );
      const edge = 1 - smoothstep(0, height * 0.025, ed);
      const inside = ptInTri(x, y, apex[0], apex[1], left[0], left[1], right[0], right[1]);
      const bands = 0.5 + 0.5 * Math.cos(((y - apex[1]) / (height * 0.65)) * 18);
      return clamp(0.06 + edge * 0.55 + (inside ? 0.16 + bands * 0.22 : 0) + wave * 0.04, 0, 1);
    }
    case 'stylobate': {
      const steps = 5;
      const bt = (y / height) * steps;
      const within = bt - Math.floor(bt);
      const edge = 1 - smoothstep(0, 0.08, Math.min(within, 1 - within));
      const va = 0.5 + 0.5 * Math.cos((x / width) * Math.PI * 14);
      return clamp(0.16 + edge * 0.55 + va * 0.12 + wave * 0.04, 0, 1);
    }
    case 'trackRecord': {
      const rng = mulberry32(seed);
      let total = 0;
      for (let i = 0; i < 9; i++) {
        const gx = width * (0.12 + rng() * 0.76);
        const gy = height * (0.18 + rng() * 0.64);
        total += (1 - smoothstep(0, minSide * 0.06, Math.hypot(x - gx, y - gy))) * 0.5;
      }
      const lineY = height * 0.78 - (x / width) * height * 0.5;
      const trend = 1 - smoothstep(0, height * 0.022, Math.abs(y - lineY));
      return clamp(0.04 + total + trend * 0.5 + wave * 0.04, 0, 1);
    }
    case 'marketLife': {
      const t = x / width;
      const wy = height * 0.5 + Math.sin(D.phase) * height * 0.22;
      const spread = height * 0.4 * (1 - t * 0.85);
      const cluster = 1 - smoothstep(0, spread, Math.abs(y - wy));
      const cl = 1 - smoothstep(0, height * 0.015, Math.abs(y - mix(height * 0.5, wy, t * t)));
      return clamp(
        0.08 + cluster * 0.6 * (0.4 + t * 0.6) + cl * 0.45 * (0.2 + t * 0.8) + wave * 0.04,
        0,
        1,
      );
    }
    case 'halfLife': {
      const t = x / width;
      const decay = Math.exp(-t * 2.6);
      const sy = cy + Math.sin(D.phase) * height * 0.15;
      const spike = 1 - smoothstep(0, maxD * 0.32, Math.hypot(x - width * 0.08, y - sy));
      const base = 0.5 + 0.5 * Math.sin(x * 0.022 + y * 0.018);
      return clamp(0.04 + decay * 0.5 + spike * 0.45 + base * 0.05 + wave * 0.03, 0, 1);
    }
    case 'ring': {
      const core = 1 - smoothstep(0, maxD * 0.42, d);
      const orbit = ringBand * (0.5 + 0.5 * Math.cos(angle * 8));
      const voidO =
        smoothstep(maxD * 0.11, maxD * 0.18, d) * (1 - smoothstep(maxD * 0.26, maxD * 0.36, d));
      return clamp(0.1 + core * 0.4 + orbit * 0.55 + voidO * 0.4 + wave * 0.08, 0, 1);
    }
    case 'bands': {
      const nb = 7;
      const t = (y / height) * nb;
      const within = t - Math.floor(t);
      const ri = Math.floor(t);
      const edge = 1 - smoothstep(0, 0.14, Math.min(within, 1 - within));
      const amp = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(ri * 1.7 + seed * 0.01));
      const sweep = 0.6 + 0.4 * Math.sin(x * 0.006 + seed * 0.002);
      return clamp(0.08 + edge * amp * sweep + wave * 0.05, 0, 1);
    }
    case 'stream': {
      const streak = 0.5 + 0.5 * Math.sin(y * 0.055 + Math.sin(x * 0.008 + seed * 0.001) * 2.2);
      const flow = 0.5 + 0.5 * Math.sin(x * 0.013 - seed * 0.002 + y * 0.002);
      return clamp(0.1 + streak * 0.42 * flow + flow * 0.18 + wave * 0.05, 0, 1);
    }
    case 'horizon': {
      const hy = cy + Math.sin(x * 0.006 + seed * 0.001) * height * 0.06;
      const band = 1 - smoothstep(0, height * 0.3, Math.abs(y - hy));
      const sh = 0.5 + 0.5 * Math.sin(x * 0.02 + seed);
      return clamp(0.05 + band * (0.55 + sh * 0.25) + wave * 0.05, 0, 1);
    }
    case 'scanlines': {
      const lines = 0.5 + 0.5 * Math.sin(y * 0.16);
      const env = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(x * 0.0055 + seed * 0.001));
      return clamp(0.07 + lines * 0.5 * env + wave * 0.05, 0, 1);
    }
    case 'ridge': {
      const layers = 5;
      const warp = Math.sin(x * 0.004 + seed * 0.001) * height * 0.04;
      const t = ((y + warp) / height) * layers;
      const within = t - Math.floor(t);
      const ridge = (1 - Math.abs(within - 0.5) * 2) ** 1.4;
      const fx = 0.6 + 0.4 * Math.sin(x * 0.009 - seed * 0.001);
      return clamp(0.09 + ridge * 0.55 * fx + wave * 0.05, 0, 1);
    }
    case 'split':
      return sourcesField(x, y, o);
    case 'breakthrough': {
      const by = cy + Math.sin(x * 0.008 + seed) * height * 0.1;
      const band = 1 - smoothstep(0, height * 0.22, Math.abs(y - by));
      const dir = smoothstep(width * 0.15, width * 0.85, x);
      const bloom = 1 - smoothstep(0, maxD * 0.45, Math.hypot(x - width * 0.72, y - height * 0.42));
      return clamp(0.08 + band * 0.6 * dir + bloom * 0.35 + wave * 0.1, 0, 1);
    }
    case 'surge':
    case 'crossfade':
      return sourcesField(x, y, o);
    case 'momentum': {
      const baseY =
        height * 0.66 -
        (x / width) * height * 0.34 +
        Math.sin(x * 0.006 + seed * 0.001) * height * 0.05;
      const band = 1 - smoothstep(0, height * 0.2, Math.abs(y - baseY));
      const grow = smoothstep(0.05, 0.95, x / width);
      const lead = 1 - smoothstep(0, maxD * 0.4, Math.hypot(x - width * 0.82, y - height * 0.32));
      return clamp(0.06 + band * 0.55 * grow + lead * 0.42 + wave * 0.06, 0, 1);
    }
    case 'converge':
    case 'radiate':
    case 'aperture':
    case 'pinwheel': {
      const N = o.convN || 4;
      const gap = o.convGap == null ? 0.18 : o.convGap;
      const rot = ((o.convRot || 0) * Math.PI) / 180;
      const twist = ((o.convTwist || 0) * Math.PI) / 180;
      const sec = (2 * Math.PI) / N;
      const halfAng = sec * 0.45;
      const rd = clamp(d / (maxD * 0.9), 0, 1);
      const aa = angle + rot + rd * twist;
      const mm = ((aa % sec) + sec) % sec;
      const da = Math.min(mm, sec - mm);
      if (mode === 'aperture') {
        const polyEdge = gap / Math.max(0.25, Math.cos(da));
        const hole = 1 - smoothstep(polyEdge - 0.02, polyEdge + 0.03, rd);
        const blade = 0.5 + 0.5 * Math.cos(aa * N);
        const field = (1 - hole) * (0.58 + 0.42 * blade) * (1 - smoothstep(0.95, 1.2, rd));
        return clamp(0.07 + field * 0.82 + wave * 0.04, 0, 1);
      }
      if (mode === 'pinwheel') {
        const tw = o.convTwist ? 1 : 1.0;
        const arm = 0.5 + 0.5 * Math.cos((angle + rot + rd * (twist + tw * 2.2)) * N);
        const body = smoothstep(gap, gap + 0.05, rd) * (1 - smoothstep(0.95, 1.2, rd));
        return clamp(0.07 + arm ** 1.3 * body * 0.8 + wave * 0.04, 0, 1);
      }
      const allow = halfAng * Math.max(0.06, rd);
      const inW = 1 - smoothstep(allow * 0.5, allow, da);
      const body = smoothstep(gap, gap + 0.05, rd) * (1 - smoothstep(0.95, 1.15, rd));
      const focus = mode === 'radiate' ? mix(0.45, 1.0, rd) : mix(0.5, 1.0, 1 - rd);
      return clamp(0.06 + inW * body * 0.82 * focus + wave * 0.04, 0, 1);
    }
    case 'chevron': {
      const period = minSide * 0.45;
      const tw = Math.abs(((((x / period) % 1) + 1) % 1) - 0.5) * 2;
      const yu = y / minSide + tw * 0.42;
      const stripe = 0.5 + 0.5 * Math.cos(yu * Math.PI * 2 * 4);
      return clamp(0.1 + stripe ** 1.8 * 0.68 + wave * 0.025, 0, 1);
    }
    case 'peaks': {
      const n = 7;
      const xi = (x / width) * n;
      const k = Math.floor(xi);
      const f = xi - k;
      const hk = 0.4 + mulberry32((seed ^ Math.imul(k + 1, 2654435761)) >>> 0)() * 0.5;
      const ph = hk * (1 - Math.abs(f - 0.5) * 2);
      const skyY = height * (1 - ph * 0.8 - 0.05);
      const fill = smoothstep(0, height * 0.025, y - skyY);
      return clamp(0.07 + fill * 0.72 + wave * 0.03, 0, 1);
    }
    case 'arrows': {
      const flowA = Math.sin(y * 0.004 + seed * 0.001) * 0.35;
      const px = x * Math.cos(flowA) + y * Math.sin(flowA);
      const py = -x * Math.sin(flowA) + y * Math.cos(flowA);
      const period = minSide * 0.18;
      const tri = Math.abs(((((py / period) % 1) + 1) % 1) - 0.5) * 2;
      const adv = px / (minSide * 0.16) - tri * 0.6;
      const arrow = 0.5 + 0.5 * Math.cos(adv * Math.PI * 2);
      return clamp(0.1 + arrow ** 2.2 * 0.7 + wave * 0.025, 0, 1);
    }
    case 'sierpinski': {
      const N = 64;
      const i = Math.floor((x / width) * N);
      const j = Math.floor((1 - y / height) * N);
      const on = (i & j) === 0 && i >= 0 && j >= 0 ? 1 : 0;
      return clamp(0.08 + on * 0.78 + wave * 0.02, 0, 1);
    }
    case 'nested': {
      const sec = (2 * Math.PI) / 3;
      const ar = angle + Math.PI / 2;
      const m = ((ar % sec) + sec) % sec;
      const triMetric = d * Math.cos(m - sec / 2);
      const rings = 0.5 + 0.5 * Math.cos(triMetric * 0.05 - seed * 0.001);
      return clamp(0.1 + rings ** 1.4 * 0.55 + wave * 0.03, 0, 1);
    }
    case 'heatmap':
      return sourcesField(x, y, o);
    case 'datastream': {
      const cols = Math.max(50, Math.round(width / 12));
      const blk = Math.floor(y / (height / 26));
      const g = Math.sin(blk * 7.7 + seed * 0.05) * 43758.5;
      const gv = g - Math.floor(g);
      const shift = gv > 0.85 ? (gv - 0.5) * 0.06 : 0;
      const ci = Math.floor((x / width + shift) * cols);
      const hh = Math.sin(ci * 127.1 + seed * 0.013) * 43758.545;
      const colV = hh - Math.floor(hh);
      const dash = 0.5 + 0.5 * Math.sin(y * 0.1 + ci * 0.6);
      const sweep = 0.4 + 0.6 * (0.5 + 0.5 * Math.sin(y * 0.012 + x * 0.002 + seed * 0.001));
      const hot = smoothstep(0.85, 0.97, colV) * 0.45;
      return clamp(0.12 + colV * 0.5 * dash * sweep + colV * 0.12 + hot + wave * 0.03, 0, 1);
    }
    case 'ticker': {
      const rows = Math.max(20, Math.round(height / 16));
      const ri = Math.floor((y / height) * rows);
      const rh = Math.sin(ri * 91.7 + seed * 0.02) * 43758.5;
      const rowV = rh - Math.floor(rh);
      const seg = 0.5 + 0.5 * Math.sin(x * 0.12 + ri * 2.1);
      const active = smoothstep(0.32, 0.6, rowV);
      const scroll = 0.5 + 0.5 * Math.sin(x * 0.01 - ri * 0.5 + seed * 0.001);
      return clamp(0.1 + active * seg * 0.55 * scroll + active * 0.12 + wave * 0.03, 0, 1);
    }
    case 'matrix': {
      const cell = o.matrixCell || 40;
      const levels = o.matrixLevels || 1;
      const fill = o.matrixFill == null ? 0.7 : o.matrixFill;
      const bias = o.matrixBias || 'scatter';
      const gx = Math.max(4, Math.round(width / cell));
      const gy = Math.max(3, Math.round(height / cell));
      const cq = Math.floor((x / width) * gx);
      const rq = Math.floor((y / height) * gy);
      const hh = Math.sin(cq * 57.3 + rq * 131.7 + seed * 0.03) * 43758.5;
      let cv = hh - Math.floor(hh);
      if (bias === 'gradient') cv = cv * 0.45 + 0.55 * (cq / Math.max(1, gx - 1));
      else if (bias === 'radial') {
        const rr = Math.hypot(cq / gx - 0.5, rq / gy - 0.5) / 0.6;
        cv = cv * 0.45 + 0.55 * (1 - clamp(rr, 0, 1));
      } else if (bias === 'diagonal') {
        cv = cv * 0.45 + 0.55 * (0.5 + 0.5 * Math.sin((cq + rq) * 0.6 + seed * 0.002));
      }
      const thr = 1 - fill;
      cv = clamp((cv - thr) / Math.max(0.001, 1 - thr), 0, 1);
      if (levels > 1) cv = Math.round(cv * (levels - 1)) / (levels - 1);
      return clamp(0.08 + cv * 0.78 + wave * 0.02, 0, 1);
    }
    case 'equalizer': {
      const cols = Math.max(30, Math.round(width / 22));
      const ci = Math.floor((x / width) * cols);
      const ch = Math.sin(ci * 73.1 + seed * 0.02) * 43758.5;
      const lvl = 0.3 + 0.6 * (ch - Math.floor(ch));
      const anim = 0.5 + 0.5 * Math.sin(ci * 0.8 + seed * 0.001);
      const barTop = height * (1 - lvl * (0.5 + 0.5 * anim));
      const fill = smoothstep(0, height * 0.05, y - barTop);
      return clamp(0.08 + fill * 0.7 + wave * 0.03, 0, 1);
    }
    case 'current': {
      const wy =
        Math.sin(x * 0.006 + seed * 0.001) * height * 0.13 +
        Math.cos(x * 0.013 - seed * 0.0007) * height * 0.05;
      const lane = (0.5 + 0.5 * Math.sin((y + wy) * 0.032)) ** 1.15;
      const flow = 0.5 + 0.5 * Math.sin(x * 0.011 - seed * 0.002);
      return clamp(0.07 + lane * 0.55 * (0.5 + flow * 0.5) + wave * 0.05, 0, 1);
    }
    case 'eddy': {
      const swirl = angle * 4 + d * 0.025 - seed * 0.001;
      const arm = (0.5 + 0.5 * Math.sin(swirl)) ** 1.2;
      const core = 1 - smoothstep(maxD * 0.04, maxD * 0.78, d);
      return clamp(0.07 + arm * 0.55 * core + core * 0.14 + wave * 0.05, 0, 1);
    }
    case 'confluence': {
      const s1 = (0.5 + 0.5 * Math.sin((x * 0.9 + y * 0.34) * 0.022 + seed * 0.001)) ** 1.1;
      const s2 = (0.5 + 0.5 * Math.sin((x * 0.9 - y * 0.34) * 0.022 - seed * 0.001)) ** 1.1;
      const t = smoothstep(0, width, x);
      const lane = mix(s1, s2, t);
      const focus = 1 - smoothstep(0, maxD, Math.abs(y - cy));
      return clamp(0.07 + lane * 0.55 + focus * 0.12 + wave * 0.05, 0, 1);
    }
    case 'cascade': {
      const diag = x * 0.42 + y * 0.91;
      const warp = Math.sin(x * 0.007 - seed * 0.001) * 55;
      const lane = (0.5 + 0.5 * Math.sin((diag + warp) * 0.022)) ** 1.25;
      const accel = smoothstep(-0.1, 1, y / height);
      return clamp(0.05 + lane * 0.6 * (0.35 + accel * 0.75) + wave * 0.05, 0, 1);
    }
    case 'braid': {
      const w1 = Math.sin(x * 0.014 + seed * 0.001) * height * 0.1;
      const w2 = -Math.sin(x * 0.014 - seed * 0.0013) * height * 0.1;
      const a = (0.5 + 0.5 * Math.sin((y + w1) * 0.045)) ** 1.2;
      const b = (0.5 + 0.5 * Math.sin((y + w2) * 0.045 + Math.PI)) ** 1.2;
      return clamp(0.07 + Math.max(a, b) * 0.55 + a * b * 0.4 + wave * 0.05, 0, 1);
    }
    case 'ripple': {
      const k = 0.034;
      const w1 = Math.cos(Math.hypot(x - width * 0.32, y - cy) * k - D.phase);
      const w2 = Math.cos(Math.hypot(x - width * 0.68, y - cy) * k - D.phase);
      return clamp(0.12 + (0.5 + (0.5 * (w1 + w2)) / 2) * 0.7 + wave * 0.04, 0, 1);
    }
    case 'resonance': {
      const n = Math.max(3, (D.outcomes || 2) + 1);
      const k = 0.03;
      const R = minSide * 0.3;
      let s = 0;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const px = cx + Math.cos(a) * R;
        const py = cy + Math.sin(a) * R;
        s += Math.cos(Math.hypot(x - px, y - py) * k - D.phase);
      }
      return clamp(0.12 + (0.5 + (0.5 * s) / n) * 0.7 + wave * 0.04, 0, 1);
    }
    case 'beat': {
      const proj = x * 0.72 + y * 0.2;
      const w = (Math.cos(proj * 0.04) + Math.cos(proj * 0.047)) / 2;
      return clamp(0.1 + (0.5 + 0.5 * w) * 0.7 + wave * 0.04, 0, 1);
    }
    case 'standing': {
      const sw = Math.cos(y * 0.03) * Math.cos(D.phase + x * 0.004);
      return clamp(0.1 + (0.5 + 0.5 * sw) * 0.66 + wave * 0.04, 0, 1);
    }
    case 'moire': {
      const k = 0.045;
      const g1 = Math.cos(d * k);
      const g2 = Math.cos(Math.hypot(x - width * 0.6, y - height * 0.45) * k * 1.08);
      return clamp(0.12 + (0.5 + (0.5 * (g1 + g2)) / 2) * 0.66 + wave * 0.04, 0, 1);
    }
    case 'cells': {
      const S = siteSet(o, 14);
      let f1 = 1e9;
      let f2 = 1e9;
      for (const p of S) {
        const dd = Math.hypot(x - p.x, y - p.y);
        if (dd < f1) {
          f2 = f1;
          f1 = dd;
        } else if (dd < f2) f2 = dd;
      }
      const edge = smoothstep(0, minSide * 0.045, (f2 - f1) * 0.5);
      return clamp(0.12 + edge * 0.62 + wave * 0.04, 0, 1);
    }
    case 'edges': {
      const S = siteSet(o, 16);
      let f1 = 1e9;
      let f2 = 1e9;
      for (const p of S) {
        const dd = Math.hypot(x - p.x, y - p.y);
        if (dd < f1) {
          f2 = f1;
          f1 = dd;
        } else if (dd < f2) f2 = dd;
      }
      const seam = 1 - smoothstep(0, minSide * 0.035, (f2 - f1) * 0.5);
      return clamp(0.06 + seam * 0.7 + wave * 0.04, 0, 1);
    }
    case 'territory': {
      const S = siteSet(o, 8);
      let f1 = 1e9;
      for (const p of S) {
        const dd = Math.hypot(x - p.x, y - p.y) / p.w;
        if (dd < f1) f1 = dd;
      }
      const core = 1 - smoothstep(0, minSide * 0.46, f1);
      return clamp(0.08 + core * 0.66 + wave * 0.04, 0, 1);
    }
    case 'crystal': {
      const S = siteSet(o, 12);
      let f1 = 1e9;
      let f2 = 1e9;
      for (const p of S) {
        const dd = Math.hypot(x - p.x, y - p.y);
        if (dd < f1) {
          f2 = f1;
          f1 = dd;
        } else if (dd < f2) f2 = dd;
      }
      const facet = smoothstep(0, minSide * 0.1, (f2 - f1) * 0.5);
      const grad = 1 - smoothstep(0, minSide * 0.32, f1);
      return clamp(0.08 + facet * 0.4 + grad * 0.3 + wave * 0.04, 0, 1);
    }
    case 'stipple': {
      const S = siteSet(o, 20);
      let acc = 0;
      for (const p of S) acc += 1 - smoothstep(0, minSide * 0.1, Math.hypot(x - p.x, y - p.y));
      const grad = 0.3 + 0.7 * smoothstep(0, width, x);
      return clamp(0.05 + Math.min(1, acc) * 0.6 * grad + wave * 0.04, 0, 1);
    }
    case 'crowd': {
      hotSet(o, 12);
      const noise = 0.2 + wave * 0.22 + vignette * 0.25;
      const pockets = Math.sin(x * 0.018 + seed * 0.01) * Math.cos(y * 0.014 - seed * 0.006);
      return clamp(noise + pockets * 0.18, 0, 1);
    }
    default: {
      const noise = 0.2 + wave * 0.22 + vignette * 0.25;
      const pockets = Math.sin(x * 0.018 + seed * 0.01) * Math.cos(y * 0.014 - seed * 0.006);
      return clamp(noise + pockets * 0.18, 0, 1);
    }
  }
}

export function sampleDotStudioBrightness(
  mode: string,
  u: number,
  v: number,
  width: number,
  height: number,
  seed: number | string,
  config?: DensityFieldConfig,
): number {
  const cfg = config ?? buildDensityConfig(mode, seed, width, height);
  const wDiv = width <= 1 ? 0 : width - 1;
  const hDiv = height <= 1 ? 0 : height - 1;
  return densityField(u * wDiv, v * hDiv, cfg);
}
